import {
  buildOrderLiteral,
  buildPagingLiteral,
  buildWhereLiteral,
  OrderBy,
} from '@/lib/graphql-query-builder';

// People are `UserProfileDetail` records (basic info, one per Blocks OS IAM user, linked by `UserId`);
// their personal and contact details live on `UserContactDetail` and their HR terms on `UserHRPeriodicalProfile`
// (with their `HRWorkDay` / `HRLeaveAllotment` items embedded), all linked by the same `UserId`. The GraphQL query field is `get<Schema>s`
// (confirmed against the @seliseblocks/client SDK's own query building), aliased to `Employees` /
// `ContactDetails` / `HrProfiles` so call sites read the view-model names.
const USER_PROFILE_DETAIL_FIELDS = `
  ItemId
  CreatedDate
  CreatedBy
  LastUpdatedDate
  LastUpdatedBy
  Language
  OrganizationId
  Tags
  UserId
  Salutation
  FirstName
  LastName
  DisplayName
  Sex
  DateOfBirth
  DateOfBirthOriginal
  EmployeeID
  JoiningDate
  DepartmentId
  DepartmentName
  DepartmentKey
  DesignationId
  Designation
  ReportingManagerUserId
  ReportingManagerName
  ProfileImageId
  CtcLevelId
  CategoryId
  Email
  PhoneNumber
  IsWFH
  WFODays
  Skills {
    Skill
    SkillName
    SubCategoryId
    Proficiency
  }
  SubCategoryExperiences {
    SubCategoryId
    SubCategory
    StartDate
    EndDate
    IsUntilNow
    TotalExperienceMonths
  }
  IsProfileCompleted
`;

/** Newest employee first by default, like the old directory list's fixed `{ CreateDate: -1 }` sort. */
export const DEFAULT_EMPLOYEES_ORDER: OrderBy[] = [{ field: 'CreatedDate', direction: 'DESC' }];

export const buildGetEmployeesQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_EMPLOYEES_ORDER
) => `
  query {
    Employees: getUserProfileDetails(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral(order)}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      hasNextPage
      hasPreviousPage
      totalCount
      totalPages
      pageSize
      pageNo
      items {
        ${USER_PROFILE_DETAIL_FIELDS}
      }
    }
  }
`;

const USER_CONTACT_DETAIL_FIELDS = `
  ItemId
  UserId
  PrivateEmail
  PrivatePhoneNumber
  IsPrivateInfoVisible
  EmergencyContact {
    Name
    Email
    PhoneNumber
    Relationship
  }
  PresentAddress {
    Address
    Country
    Canton
    CantonId
    City
    ZipCode
  }
  IsSameAsPresentAddress
  PermanentAddress {
    Address
    Country
    Canton
    CantonId
    City
    ZipCode
  }
  LinkedInProfile
  GithubProfile
  FaceBookProfile
  WhatsAppProfile
  InstagramProfile
  ProfessionalImageUrl
  IsReferenceDetailsEnabled
  References {
    Name
    Designation
    Company
    Phone
    Email
  }
  FathersName
  MothersName
  SpouseName
  BloodGroup
  PassportNo
  PassportExpiryDate
  HobbiesInterests
  PreviousExperience
  PreviousOrganization
  LatestAcademicTitle
  GraduationInstitute
  PostGraduationInstitute
  Nid
  Etin
`;

export const buildGetContactDetailsQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    ContactDetails: getUserContactDetails(
      where: ${buildWhereLiteral(where)}
      order: []
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      items {
        ${USER_CONTACT_DETAIL_FIELDS}
      }
    }
  }
`;

export const buildGetHrProfilesQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    HrProfiles: getUserHRPeriodicalProfiles(
      where: ${buildWhereLiteral(where)}
      order: []
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      items {
        ItemId
        UserId
        EmploymentType
        HRPeriodStartDate
        HRPeriodEndDate
        IsFlexible
        AvgDailyWorkHour
        IsCurrentHrYear
        HRPeriod
        LeaveCarryForwardDays
        ToilCarryForwardDays
        SpecialToilCarryForwardDays
        WorkDays {
          WorkDay
          WorkingHours
          EffectiveFrom
        }
        LeaveAllotments {
          LeaveCategoryId
          LeaveCategory
          LeaveLimitTotalDays
          IsApplicable
          IsUnlimited
          IsDefault
        }
      }
    }
  }
`;

/** Only each term's employment type and period, for the list's Employee Type column. */
export const buildGetEmploymentTypesQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    HrProfiles: getUserHRPeriodicalProfiles(
      where: ${buildWhereLiteral(where)}
      order: []
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      totalCount
      items {
        UserId
        EmploymentType
        HRPeriodStartDate
        IsCurrentHrYear
      }
    }
  }
`;

export const buildGetDesignationsLookupQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    Designations: getDesignations(
      where: ${buildWhereLiteral(where)}
      order: []
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      totalCount
      items {
        ItemId
        Title
      }
    }
  }
`;
